
import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { PropertyLocationSection } from "@/components/properties/PropertyLocationSection";
import { PropertyDetailsSection } from "@/components/properties/PropertyDetailsSection";
import { FinancialDetailsSection } from "@/components/properties/FinancialDetailsSection";
import { PropertyImagesSection } from "@/components/properties/PropertyImagesSection";
import { PropertyVideoSection } from "@/components/properties/PropertyVideoSection";
import { usePropertyForm } from "@/hooks/usePropertyForm";
import { createProperty, saveDraftProperty } from "@/services/propertyService";
import { RentCastPropertyData } from "@/types/rentcast";

const NewPropertyPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ completed: number; total: number } | null>(null);

  // Refresh guard while photos are uploading
  useEffect(() => {
    if (!uploadProgress) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploadProgress]);
  

  
  // Use our custom hook to manage all form state
  const form = usePropertyForm();
  
  // Map RentCast property types to kebab-case form values
  const mapPropertyType = (type: string): string => {
    const typeLower = type.toLowerCase();
    const mapping: Record<string, string> = {
      'single family': 'single-family',
      'multi family': 'multi-family',
      'duplex': 'multi-family',
      'triplex': 'multi-family',
      'quadruplex': 'multi-family',
      'condo': 'condo',
      'condominium': 'condo',
      'townhouse': 'townhouse',
      'land': 'land',
      'commercial': 'commercial',
    };
    return mapping[typeLower] || type.toLowerCase().replace(/\s+/g, '-');
  };

  // Map RentCast architecture types to our form values (lowercase/kebab-case)
  const mapArchitectureStyle = (style: string): string => {
    const styleLower = style.toLowerCase();
    const mapping: Record<string, string> = {
      'colonial': 'colonial',
      'ranch': 'ranch',
      'cape cod': 'cape-cod',
      'bungalow': 'bungalow',
      'tudor': 'tudor',
      'victorian': 'victorian',
      'craftsman': 'craftsman',
      'modern': 'modern',
      'contemporary': 'modern',
      'mediterranean': 'mediterranean',
    };
    return mapping[styleLower] || styleLower;
  };

  // Map RentCast structure types to our foundation types
  const mapFoundationType = (structure: string): string => {
    const structureLower = structure.toLowerCase();
    if (structureLower.includes('slab')) return 'Slab';
    if (structureLower.includes('crawl')) return 'Crawl Space';
    if (structureLower.includes('basement')) return 'Basement';
    return structure;
  };

  // Handle imported property data from RentCast
  const handleImportPropertyData = (data: RentCastPropertyData) => {
    if (data.bedrooms) form.setBedrooms(String(data.bedrooms));
    if (data.bathrooms) form.setBathrooms(String(data.bathrooms));
    if (data.squareFootage) form.setSquareFeet(String(data.squareFootage));
    if (data.propertyType) form.setPropertyType(mapPropertyType(data.propertyType));
    if (data.yearBuilt) form.setYearBuilt(String(data.yearBuilt));
    if (data.lotSize) form.setLotSize(String(data.lotSize));
    if (data.architectureType) form.setArchitecture(mapArchitectureStyle(data.architectureType));
    if (data.structure) form.setFoundationType(mapFoundationType(data.structure));
    
    toast({
      title: "Property data imported",
      description: "Auto-filled fields can be edited as needed.",
    });
  };
  
  // Handle save as draft (no validation required)
  const handleSaveDraft = async () => {
    if (!user) {
      toast({
        title: "Authentication Error",
        description: "You must be logged in to save a draft",
        variant: "destructive",
      });
      return;
    }

    setIsSavingDraft(true);
    try {
      const partialData = form.createPropertyData(user.id);
      const filesToUpload = form.getFilesForUpload();
      if (filesToUpload.length > 0) {
        setUploadProgress({ completed: 0, total: filesToUpload.length });
      }
      const result = await saveDraftProperty(user.id, partialData, filesToUpload, (completed, total) => {
        setUploadProgress({ completed, total });
      });

      if (result.success) {
        toast({
          title: "Draft saved",
          description: "Your draft has been saved. You can finish it anytime from the Drafts tab.",
        });
        navigate("/seller/properties/active");
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      toast({
        title: "Error",
        description: "Failed to save draft. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSavingDraft(false);
      setUploadProgress(null);
    }
  };


  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Authentication Error",
        description: "You must be logged in to create a property listing",
        variant: "destructive",
      });
      return;
    }

    // Validate form including images
    const validationErrors = form.validateForm();
    if (validationErrors) {
      const errorMessages = Object.values(validationErrors).join('. ');
      toast({
        title: "Validation Error",
        description: errorMessages,
        variant: "destructive",
      });
      
      // Scroll to the first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`) || 
                      document.querySelector(`[data-tour="${firstErrorField}-section"]`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Create the property data object
      const propertyData = form.createPropertyData(user.id);
      
      // Convert preloaded images to fresh File objects to prevent stale references
      // This is critical for mobile browsers where File handles can become invalid
      const filesToUpload = form.getFilesForUpload();
      if (filesToUpload.length > 0) {
        setUploadProgress({ completed: 0, total: filesToUpload.length });
      }
      
      // Save the property and handle image uploads
      const result = await createProperty(propertyData, filesToUpload, (completed, total) => {
        setUploadProgress({ completed, total });
      });
      
      if (result.success && result.propertyId) {
        toast({
          title: "Property saved",
          description: "Your property listing has been created. You can add a video from the Edit page.",
        });
        navigate("/seller/properties/active");
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Error saving property:", error);
      toast({
        title: "Error",
        description: "Failed to save property. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setUploadProgress(null);
    }
  };

  
  return (
    <div className="space-y-6 pb-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Upload New Deal</h1>
        <p className="text-muted-foreground">
          Create a new property listing to market to investors
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Property Location Section */}
        <div data-tour="location-section">
        <PropertyLocationSection
          address={form.address}
          setAddress={form.setAddress}
          city={form.city}
          setCity={form.setCity}
          state={form.state}
          setState={form.setState}
          zipCode={form.zipCode}
          setZipCode={form.setZipCode}
          errors={form.errors}
          clearFieldError={form.clearFieldError}
        />
        </div>
        
        {/* Property Details Section */}
        <div data-tour="details-section">
        <PropertyDetailsSection
          bedrooms={form.bedrooms}
          setBedrooms={form.setBedrooms}
          bathrooms={form.bathrooms}
          setBathrooms={form.setBathrooms}
          squareFeet={form.squareFeet}
          setSquareFeet={form.setSquareFeet}
          propertyType={form.propertyType}
          setPropertyType={form.setPropertyType}
          units={form.units}
          setUnits={form.setUnits}

          foundationType={form.foundationType}
          setFoundationType={form.setFoundationType}
          yearBuilt={form.yearBuilt}
          setYearBuilt={form.setYearBuilt}
          lotSize={form.lotSize}
          setLotSize={form.setLotSize}
          roofAge={form.roofAge}
          setRoofAge={form.setRoofAge}
          windowType={form.windowType}
          setWindowType={form.setWindowType}
          furnaceAge={form.furnaceAge}
          setFurnaceAge={form.setFurnaceAge}
          waterHeaterAge={form.waterHeaterAge}
          setWaterHeaterAge={form.setWaterHeaterAge}
          architecture={form.architecture}
          setArchitecture={form.setArchitecture}
          parking={form.parking}
          setParking={form.setParking}
          address={form.address}
          city={form.city}
          state={form.state}
          zipCode={form.zipCode}
          onImportData={handleImportPropertyData}
          errors={form.errors}
          clearFieldError={form.clearFieldError}
        />
        </div>
        
        {/* Financial Details Section */}
        <div data-tour="financial-section">
        <FinancialDetailsSection
          price={form.price}
          setPrice={form.setPrice}
          arv={form.arv}
          setArv={form.setArv}
          zillowLink={form.zillowLink}
          setZillowLink={form.setZillowLink}
          contractDate={form.contractDate}
          setContractDate={form.setContractDate}
          errors={form.errors}
          clearFieldError={form.clearFieldError}
        />
        </div>
        
        {/* Property Description and Images Section */}
        <div data-tour="images-section">
          <PropertyImagesSection
            description={form.description}
            setDescription={form.setDescription}
            imagePreviewUrls={form.imagePreviewUrls}
            selectedImages={form.selectedImages}
            handleImageChange={form.handleImageChange}
            removeImage={form.removeImage}
            reorderImages={form.reorderImages}
            errors={form.errors}
            clearFieldError={form.clearFieldError}
            uploadProgress={uploadProgress}

          />
        </div>
        
        {/* Property Video Section */}
        <div data-tour="video-section">
        <PropertyVideoSection />
        </div>
        
        {/* Submit Section */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/seller/dashboard")}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleSaveDraft}
            disabled={isSavingDraft || isLoading}
          >
            {isSavingDraft ? "Saving Draft..." : "Save Draft"}
          </Button>
          <Button
            type="submit"
            className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
            disabled={isLoading || isSavingDraft}
            data-tour="save-property"
          >
            {isLoading
              ? uploadProgress
                ? `Uploading photos ${uploadProgress.completed}/${uploadProgress.total}…`
                : "Saving..."
              : "Save Property"}

          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewPropertyPage;
